'use client';

import { useState } from 'react';
import { ChevronRight, ChevronDown, Copy, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

interface JsonViewerProps {
  data: unknown;
  collapsed?: boolean;
  maxDepth?: number;
  className?: string;
}

export function JsonViewer({
  data,
  collapsed = false,
  maxDepth = 10,
  className,
}: JsonViewerProps) {
  return (
    <div className={cn('font-mono text-sm overflow-auto', className)}>
      <JsonNode value={data} depth={0} maxDepth={maxDepth} defaultCollapsed={collapsed} />
    </div>
  );
}

interface JsonNodeProps {
  value: unknown;
  depth: number;
  maxDepth: number;
  defaultCollapsed: boolean;
  keyName?: string;
}

function JsonNode({ value, depth, maxDepth, defaultCollapsed, keyName }: JsonNodeProps) {
  const [isCollapsed, setIsCollapsed] = useState(defaultCollapsed && depth > 0);
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    await navigator.clipboard.writeText(JSON.stringify(value, null, 2));
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (depth >= maxDepth) {
    return <span className="text-muted-foreground">...</span>;
  }

  // Render key prefix
  const keyPrefix = keyName !== undefined ? (
    <span style={{ color: 'var(--json-key)' }}>&quot;{keyName}&quot;</span>
  ) : null;

  // Primitives
  if (value === null) {
    return (
      <span>
        {keyPrefix}{keyPrefix && ': '}
        <span style={{ color: 'var(--json-null)' }}>null</span>
      </span>
    );
  }

  if (value === undefined) {
    return (
      <span>
        {keyPrefix}{keyPrefix && ': '}
        <span style={{ color: 'var(--json-null)' }}>undefined</span>
      </span>
    );
  }

  if (typeof value === 'boolean') {
    return (
      <span>
        {keyPrefix}{keyPrefix && ': '}
        <span style={{ color: 'var(--json-boolean)' }}>{value.toString()}</span>
      </span>
    );
  }

  if (typeof value === 'number') {
    return (
      <span>
        {keyPrefix}{keyPrefix && ': '}
        <span style={{ color: 'var(--json-number)' }}>{value}</span>
      </span>
    );
  }

  if (typeof value === 'string') {
    return (
      <span>
        {keyPrefix}{keyPrefix && ': '}
        <span style={{ color: 'var(--json-string)' }}>&quot;{value}&quot;</span>
      </span>
    );
  }

  // Arrays
  if (Array.isArray(value)) {
    if (value.length === 0) {
      return (
        <span>
          {keyPrefix}{keyPrefix && ': '}
          <span className="text-muted-foreground">[]</span>
        </span>
      );
    }

    return (
      <div>
        <span className="inline-flex items-center gap-1">
          <Button
            variant="ghost"
            size="sm"
            className="h-4 w-4 p-0"
            onClick={() => setIsCollapsed(!isCollapsed)}
          >
            {isCollapsed ? (
              <ChevronRight className="size-3" />
            ) : (
              <ChevronDown className="size-3" />
            )}
          </Button>
          {keyPrefix}{keyPrefix && ': '}
          <span className="text-muted-foreground">
            [{isCollapsed && <span>{value.length} items</span>}
          </span>
          {!isCollapsed && (
            <Button
              variant="ghost"
              size="sm"
              className="h-4 w-4 p-0 ml-1"
              onClick={handleCopy}
              title="Copy"
            >
              {copied ? <Check className="size-3" style={{ color: 'var(--json-string)' }} /> : <Copy className="size-3" />}
            </Button>
          )}
        </span>
        {!isCollapsed && (
          <>
            <div className="pl-4 border-l border-border ml-1.5">
              {value.map((item, index) => (
                <div key={index} className="py-0.5">
                  <span className="text-muted-foreground mr-1">{index}:</span>
                  <JsonNode
                    value={item}
                    depth={depth + 1}
                    maxDepth={maxDepth}
                    defaultCollapsed={defaultCollapsed}
                  />
                  {index < value.length - 1 && <span className="text-muted-foreground">,</span>}
                </div>
              ))}
            </div>
            <span className="text-muted-foreground">]</span>
          </>
        )}
        {isCollapsed && <span className="text-muted-foreground">]</span>}
      </div>
    );
  }

  // Objects
  if (typeof value === 'object') {
    const entries = Object.entries(value as Record<string, unknown>);
    if (entries.length === 0) {
      return (
        <span>
          {keyPrefix}{keyPrefix && ': '}
          <span className="text-muted-foreground">{'{}'}</span>
        </span>
      );
    }

    return (
      <div>
        <span className="inline-flex items-center gap-1">
          <Button
            variant="ghost"
            size="sm"
            className="h-4 w-4 p-0"
            onClick={() => setIsCollapsed(!isCollapsed)}
          >
            {isCollapsed ? (
              <ChevronRight className="size-3" />
            ) : (
              <ChevronDown className="size-3" />
            )}
          </Button>
          {keyPrefix}{keyPrefix && ': '}
          <span className="text-muted-foreground">
            {'{'}{isCollapsed && <span>{entries.length} fields</span>}
          </span>
          {!isCollapsed && (
            <Button
              variant="ghost"
              size="sm"
              className="h-4 w-4 p-0 ml-1"
              onClick={handleCopy}
              title="Copy"
            >
              {copied ? <Check className="size-3" style={{ color: 'var(--json-string)' }} /> : <Copy className="size-3" />}
            </Button>
          )}
        </span>
        {!isCollapsed && (
          <>
            <div className="pl-4 border-l border-border ml-1.5">
              {entries.map(([key, val], index) => (
                <div key={key} className="py-0.5">
                  <JsonNode
                    value={val}
                    depth={depth + 1}
                    maxDepth={maxDepth}
                    defaultCollapsed={defaultCollapsed}
                    keyName={key}
                  />
                  {index < entries.length - 1 && <span className="text-muted-foreground">,</span>}
                </div>
              ))}
            </div>
            <span className="text-muted-foreground">{'}'}</span>
          </>
        )}
        {isCollapsed && <span className="text-muted-foreground">{'}'}</span>}
      </div>
    );
  }

  return <span>{String(value)}</span>;
}
